import { HStack, Modal, Text, VStack } from 'components/ui';
import { Box } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { Affiliate, AffiliateCareModel } from '../../models/affiliate';

const bullets = (careMemberLabel: string = 'dietitian') => [
  {
    clinicalOnly: true,
    parts: [
      { text: 'You need' },
      { text: 'clinical care', bold: true },
      {
        text: "— whether that's labs, testing, or medication management…",
      },
    ],
  },

  {
    parts: [
      {
        text: "You've been dealing with",
      },
      { text: 'digestive issues or conditions', bold: true },
      {
        text: "for a while, you've tried everything and still feel like symptoms run your life…",
      },
    ],
  },

  {
    parts: [
      { text: 'You have' },
      { text: 'mystery symptoms', bold: true },
      { text: 'or' },
      { text: 'discomfort', bold: true },
      { text: "but don't know where to start…" },
    ],
  },

  {
    parts: [
      {
        text: 'You want to work with a',
      },
      { text: careMemberLabel, bold: true },
      {
        text: 'to confirm your food triggers or feel confident expanding your diet…',
      },
    ],
  },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  affiliate?: Affiliate;
};

/** The variant name carries the bullet count, so both read from here. */
export const getModalVariant = (affiliate?: Affiliate) =>
  !affiliate || affiliate.care_model === AffiliateCareModel.collaborative
    ? 'clinical_4'
    : 'coach_3';

export const WhatIsAybleModal = ({ isOpen, onClose, affiliate }: Props) => {
  const isClinical = getModalVariant(affiliate) === 'clinical_4';
  const visibleBullets = (
    isClinical ? bullets() : bullets('digestive health coach')
  )
    .filter(({ clinicalOnly }) => isClinical || !clinicalOnly)
    .map(({ parts }) => parts);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      centered
      overlayColor={'rgba(45, 102, 111, 0.5)'}
    >
      <Modal.Content
        bgColor={'white'}
        borderTop={'6px solid'}
        borderColor={'secondary.500'}
        padding={{ base: 4, sm: 7 }}
      >
        <Modal.Header
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          fontSize={{ base: '21px', sm: '24px' }}
          mb={3}
          p={0}
        >
          Is Ayble right for me?
          <Modal.CloseButton
            position={'relative'}
            bg={'blueGray.100'}
            borderRadius={'full'}
            p={4}
            top={0}
            right={0}
          >
            <X size={16} />
          </Modal.CloseButton>
        </Modal.Header>
        <Modal.Body p={0} mt={3}>
          <VStack gap={4}>
            <Text fontSize={'14.5px'}>
              Ayble is built for people experiencing any of the following:
            </Text>
            {visibleBullets.map((bullet, bulletIndex) => {
              return (
                <HStack key={bulletIndex} align={'flex-start'} gap={2}>
                  <Box
                    boxSize={'7px'}
                    borderRadius={'full'}
                    bg={'secondary.200'}
                    mt={'9px'}
                    flexShrink={0}
                  />

                  <Text fontSize={'14.5px'} lineHeight={'24px'}>
                    {bullet.map(
                      (
                        { text, bold }: { text: string; bold?: boolean },
                        index
                      ) => (
                        <Text
                          as={'span'}
                          fontSize={'14.5px'}
                          fontWeight={bold ? 700 : 400}
                          key={index}
                        >
                          {index > 0 ? ` ${text}` : text}
                        </Text>
                      )
                    )}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

export default WhatIsAybleModal;
